import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { API_URL } from "../lib/utils";
import { PinDialog } from "../components/auth/PinDialog";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  Shield,
  ShieldCheck,
  UserCog,
  ChefHat,
  Coffee,
  Edit,
  Plus,
  Mail,
  Lock,
  User,
  Briefcase,
  Landmark,
  Calculator,
  ShoppingBag,
  Warehouse,
  BadgeDollarSign,
  KeyRound,
  Wallet,
  Truck,
  Wrench,
} from "lucide-react";

const ROLES = [
  { value: "admin", label: "Administrador", icon: ShieldCheck, color: "bg-red-500/20 text-red-500" },
  { value: "director", label: "Diretor", icon: Briefcase, color: "bg-rose-500/20 text-rose-500" },
  { value: "manager", label: "Gerente", icon: Shield, color: "bg-purple-500/20 text-purple-500" },
  { value: "finance", label: "Financeiro", icon: Landmark, color: "bg-emerald-500/20 text-emerald-600" },
  { value: "accountant", label: "Contabilista", icon: Calculator, color: "bg-teal-500/20 text-teal-600" },
  { value: "purchasing", label: "Comprador", icon: ShoppingBag, color: "bg-orange-500/20 text-orange-500" },
  { value: "warehouse", label: "Fiel de Armazém", icon: Warehouse, color: "bg-yellow-500/20 text-yellow-600" },
  { value: "sales", label: "Vendedor/Comercial", icon: BadgeDollarSign, color: "bg-cyan-500/20 text-cyan-600" },
  { value: "cashier", label: "Caixa", icon: Wallet, color: "bg-lime-500/20 text-lime-600" },
  { value: "hr", label: "Recursos Humanos", icon: Users, color: "bg-pink-500/20 text-pink-500" },
  { value: "driver", label: "Motorista", icon: Truck, color: "bg-indigo-500/20 text-indigo-500" },
  { value: "maintenance", label: "Manutenção", icon: Wrench, color: "bg-stone-500/20 text-stone-500" },
  { value: "staff", label: "Staff", icon: UserCog, color: "bg-blue-500/20 text-blue-500" },
  { value: "kitchen", label: "Cozinha", icon: ChefHat, color: "bg-amber-500/20 text-amber-500" },
];

const EmployeesPage = () => {
  const { t } = useLanguage();
  const { token, isAdmin, isManager, user: currentUser } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinTarget, setPinTarget] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editForm, setEditForm] = useState({
    role: "",
    is_active: true,
  });
  const [addForm, setAddForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "staff",
  });
  const [saving, setSaving] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchEmployees();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees`, {
        headers,
        withCredentials: true,
      });
      setEmployees(response.data);
    } catch (error) {
      toast.error("Erro ao carregar funcionários");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setEditForm({
      role: employee.role,
      is_active: true, // We don't have this in the response, assume active
    });
    setEditModal(true);
  };

  const handleSave = async () => {
    try {
      // Update role
      await axios.put(
        `${API_URL}/employees/${selectedEmployee.user_id}/role?role=${editForm.role}`,
        {},
        { headers, withCredentials: true }
      );

      // Update status if admin
      if (isAdmin) {
        await axios.put(
          `${API_URL}/employees/${selectedEmployee.user_id}/status?is_active=${editForm.is_active}`,
          {},
          { headers, withCredentials: true }
        );
      }

      toast.success("Funcionário atualizado!");
      setEditModal(false);
      fetchEmployees();
    } catch (error) {
      toast.error("Erro ao atualizar funcionário");
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    
    if (!addForm.name.trim() || !addForm.email.trim() || !addForm.password.trim()) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (addForm.password.length < 6) {
      toast.error("A palavra-passe deve ter pelo menos 6 caracteres");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/auth/create-employee`,
        addForm,
        { headers, withCredentials: true }
      );
      toast.success(`Funcionário "${addForm.name}" criado com sucesso!`);
      setAddModal(false);
      setAddForm({ name: "", email: "", password: "", role: "staff" });
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar funcionário");
    } finally {
      setSaving(false);
    }
  };

  const getRoleIcon = (role) => ROLES.find((r) => r.value === role)?.icon || UserCog;

  const getRoleColor = (role) => ROLES.find((r) => r.value === role)?.color || "bg-slate-500/20 text-slate-500";

  const roles = ROLES;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">{t("employees")}</h1>
          <p className="text-muted-foreground">Gestão da equipa do restaurante</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setAddModal(true)} className="gap-2" data-testid="add-employee-btn">
            <Plus className="w-4 h-4" />
            Adicionar Funcionário
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {roles
          .filter((role) => employees.some((e) => e.role === role.value))
          .map((role) => {
          const count = employees.filter((e) => e.role === role.value).length;
          const Icon = getRoleIcon(role.value);
          return (
            <Card key={role.value}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getRoleColor(role.value)}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs text-muted-foreground">{role.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Employees Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {employees.map((employee) => {
          const RoleIcon = getRoleIcon(employee.role);
          const isCurrentUser = employee.user_id === currentUser?.user_id;

          return (
            <Card
              key={employee.user_id}
              className={isCurrentUser ? "ring-2 ring-primary" : ""}
              data-testid={`employee-${employee.user_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={employee.picture} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {employee.name?.charAt(0) || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        {employee.name}
                        {isCurrentUser && (
                          <Badge variant="outline" className="text-xs">
                            Você
                          </Badge>
                        )}
                      </h3>
                      <p className="text-sm text-muted-foreground">{employee.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    {isManager && (
                      <Button
                        variant="ghost"
                        size="icon"
                        title={employee.has_pin ? "Repor / remover PIN" : "Definir PIN de 4 dígitos"}
                        onClick={() => { setPinTarget(employee); setPinOpen(true); }}
                        data-testid={`employee-pin-btn-${employee.user_id}`}
                      >
                        <KeyRound className={`w-4 h-4 ${employee.has_pin ? "text-emerald-600" : "text-muted-foreground"}`} />
                      </Button>
                    )}
                    {isAdmin && !isCurrentUser && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(employee)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <Badge className={getRoleColor(employee.role)}>
                    <RoleIcon className="w-3 h-3 mr-1" />
                    {roles.find((r) => r.value === employee.role)?.label || employee.role}
                  </Badge>
                  <Badge variant="outline" className={`text-xs ${employee.has_pin ? "border-emerald-500/40 text-emerald-700" : "border-amber-500/40 text-amber-700"}`} data-testid={`employee-pin-badge-${employee.user_id}`}>
                    <KeyRound className="w-3 h-3 mr-1" /> {employee.has_pin ? "PIN definido" : "Sem PIN"}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {employees.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Users className="w-12 h-12 mb-4 opacity-50" />
            <p>Nenhum funcionário encontrado</p>
          </div>
        )}
      </div>

      <PinDialog open={pinOpen} onOpenChange={setPinOpen} employee={pinTarget} hasPin={!!pinTarget?.has_pin} onSaved={fetchEmployees} />

      {/* Edit Modal */}
      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent data-testid="edit-employee-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              Editar Funcionário
            </DialogTitle>
          </DialogHeader>

          {selectedEmployee && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                <Avatar>
                  <AvatarImage src={selectedEmployee.picture} />
                  <AvatarFallback>{selectedEmployee.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{selectedEmployee.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedEmployee.email}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Cargo</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(value) => setEditForm({ ...editForm, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isAdmin && (
                <div className="flex items-center justify-between">
                  <Label>Conta Ativa</Label>
                  <Switch
                    checked={editForm.is_active}
                    onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditModal(false)}>
              {t("cancel")}
            </Button>
            <Button onClick={handleSave} data-testid="save-employee-btn">
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Employee Modal */}
      <Dialog open={addModal} onOpenChange={setAddModal}>
        <DialogContent className="sm:max-w-md" data-testid="add-employee-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Adicionar Funcionário
            </DialogTitle>
            <DialogDescription>
              Crie um novo funcionário para a sua empresa
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddEmployee} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Completo *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={addForm.name}
                  onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  placeholder="Maria Silva"
                  className="pl-10"
                  required
                  data-testid="add-employee-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                  placeholder="maria@empresa.ao"
                  className="pl-10"
                  required
                  data-testid="add-employee-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Palavra-passe *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password"
                  value={addForm.password}
                  onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                  placeholder="••••••••"
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="add-employee-password"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cargo</Label>
              <Select
                value={addForm.role}
                onValueChange={(value) => setAddForm({ ...addForm, role: value })}
              >
                <SelectTrigger data-testid="add-employee-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => {
                    const Icon = role.icon;
                    return (
                      <SelectItem key={role.value} value={role.value}>
                        <span className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {role.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setAddModal(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                data-testid="submit-add-employee"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    A criar...
                  </span>
                ) : (
                  "Criar Funcionário"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
